import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = window.__BACKEND_URL__;
const AX = { withCredentials: true };

/**
 * useConversationActions — shared conversation-level actions.
 *
 * Consolidates the chat-level API calls (nudge, archive, mute, delete,
 * block, accept/decline, leave group, etc.) that used to be duplicated
 * across MessagesPage.js and the floating ChatWindow.js pop-out.
 *
 * Design rules:
 * - Hook owns the API call, busy state, toast feedback and `availableActions`.
 * - Confirmation dialogs (block/delete/leave) stay in the consuming
 *   component so existing visual patterns are preserved.
 * - Two refresh callbacks:
 *     onAfterMutate(conversation, { action })   — convo still relevant
 *     onConversationGone(conversation, { action }) — convo should close/leave view
 * - Each action returns a boolean success flag (true on 2xx, false on error)
 *   so callers can branch (e.g. close dialog on success).
 *
 * Usage:
 *   const {
 *     busy,
 *     availableActions,
 *     nudge, archive, unarchive,
 *     deleteForMe, restoreForMe,
 *     block, toggleMute,
 *     accept, decline,
 *     leaveGroup,
 *   } = useConversationActions(conversation, {
 *     currentUserId: user?.id,
 *     onAfterMutate,
 *     onConversationGone,
 *   });
 */
export default function useConversationActions(
  conversation,
  {
    currentUserId = null,
    onAfterMutate = null,
    onConversationGone = null,
  } = {},
) {
  const [busy, setBusy] = useState(false);

  // Mirror latest callbacks/conversation in refs so the returned action
  // functions stay stable for consumers that memoise downstream. Updated
  // in an effect to avoid mutating refs during render.
  const onAfterMutateRef = useRef(onAfterMutate);
  const onConversationGoneRef = useRef(onConversationGone);
  const conversationRef = useRef(conversation);

  useEffect(() => {
    onAfterMutateRef.current = onAfterMutate;
  }, [onAfterMutate]);

  useEffect(() => {
    onConversationGoneRef.current = onConversationGone;
  }, [onConversationGone]);

  useEffect(() => {
    conversationRef.current = conversation;
  }, [conversation]);

  const conversationId = conversation?.id || null;
  const isMuted = !!conversation?.muted;

  const otherFirstName = useMemo(() => {
    if (!conversation || conversation.is_group) return '';
    const peer = (conversation.participants || []).find(
      (p) => p?.user_id && p.user_id !== currentUserId,
    );
    const name = String(peer?.name || peer?.email || '').trim();
    if (!name) return '';
    return name.split(/\s+/)[0];
  }, [conversation, currentUserId]);

  /**
   * Centralised API-call wrapper.
   *
   * mode:
   *   'after-mutate'      — call onAfterMutate on success
   *   'conversation-gone' — call onConversationGone on success
   *   'silent'            — no auto refresh callback
   *
   * Per-call options:
   *   silent: true — suppress the success toast (for callers that
   *                  want to render their own, e.g. a toast with an
   *                  Undo action button). Error toasts still fire so
   *                  failures are never hidden.
   */
  const runAction = useCallback(
    async ({
      action,
      method = 'post',
      url,
      body = undefined,
      successToast,
      errorToast,
      mode = 'after-mutate',
      silent = false,
    }) => {
      const convo = conversationRef.current;
      if (!convo?.id) return false;

      setBusy(true);

      try {
        const fullUrl = `${API_URL}${url}`;

        if (method === 'post') {
          await axios.post(fullUrl, body ?? {}, AX);
        } else if (method === 'patch') {
          await axios.patch(fullUrl, body ?? {}, AX);
        } else if (method === 'delete') {
          await axios.delete(fullUrl, AX);
        } else if (method === 'get') {
          await axios.get(fullUrl, AX);
        }

        if (successToast && !silent) toast.success(successToast);

        if (mode === 'after-mutate') {
          await Promise.resolve(onAfterMutateRef.current?.(convo, { action }));
        } else if (mode === 'conversation-gone') {
          await Promise.resolve(onConversationGoneRef.current?.(convo, { action }));
        }

        return true;
      } catch (err) {
        const detail = err?.response?.data?.detail;
        toast.error(detail || errorToast || 'Action failed');
        return false;
      } finally {
        setBusy(false);
      }
    },
    [],
  );

  // ── Individual actions ────────────────────────────────────────────────
  // Each action accepts an optional `{ silent }` opt-out so the caller
  // can render its own toast (e.g. with an Undo action button) without
  // duplicating the centralised one.
  const nudge = useCallback(
    (opts = {}) =>
      runAction({
        action: 'nudge',
        method: 'post',
        url: `/api/conversations/${conversationId}/nudge`,
        successToast: 'Nudge sent',
        errorToast: 'Could not send nudge',
        mode: 'after-mutate',
        silent: !!opts.silent,
      }),
    [runAction, conversationId],
  );

  const archive = useCallback(
    (opts = {}) =>
      runAction({
        action: 'archive',
        method: 'post',
        url: `/api/conversations/${conversationId}/archive`,
        successToast: 'Conversation archived',
        errorToast: 'Could not archive',
        mode: 'conversation-gone',
        silent: !!opts.silent,
      }),
    [runAction, conversationId],
  );

  const unarchive = useCallback(
    (opts = {}) =>
      runAction({
        action: 'unarchive',
        method: 'post',
        url: `/api/conversations/${conversationId}/unarchive`,
        successToast: 'Conversation restored',
        errorToast: 'Could not unarchive',
        mode: 'after-mutate',
        silent: !!opts.silent,
      }),
    [runAction, conversationId],
  );

  const deleteForMe = useCallback(
    (opts = {}) =>
      runAction({
        action: 'delete-for-me',
        method: 'delete',
        url: `/api/conversations/${conversationId}/for-me`,
        successToast: 'Removed from your inbox',
        errorToast: 'Could not remove',
        mode: 'conversation-gone',
        silent: !!opts.silent,
      }),
    [runAction, conversationId],
  );

  const restoreForMe = useCallback(
    (opts = {}) =>
      runAction({
        action: 'restore-for-me',
        method: 'post',
        url: `/api/conversations/${conversationId}/restore-for-me`,
        successToast: 'Conversation restored',
        errorToast: 'Could not restore',
        mode: 'after-mutate',
        silent: !!opts.silent,
      }),
    [runAction, conversationId],
  );

  const block = useCallback(
    (opts = {}) =>
      runAction({
        action: 'block',
        method: 'patch',
        url: `/api/conversations/${conversationId}`,
        body: { action: 'block' },
        successToast: otherFirstName ? `${otherFirstName} blocked` : 'Conversation blocked',
        errorToast: 'Could not block',
        mode: 'conversation-gone',
        silent: !!opts.silent,
      }),
    [runAction, conversationId, otherFirstName],
  );

  const unblock = useCallback(
    (opts = {}) =>
      runAction({
        action: 'unblock',
        method: 'patch',
        url: `/api/conversations/${conversationId}`,
        body: { action: 'unblock' },
        successToast: otherFirstName ? `${otherFirstName} unblocked` : 'Conversation unblocked',
        errorToast: 'Could not unblock',
        // Use after-mutate so the convo is refreshed in-place (it
        // returns to active status — we want it to STAY selected and
        // visible, not be cleared like 'conversation-gone' would do).
        mode: 'after-mutate',
        silent: !!opts.silent,
      }),
    [runAction, conversationId, otherFirstName],
  );

  const toggleMute = useCallback(
    (opts = {}) =>
      runAction({
        action: isMuted ? 'unmute' : 'mute',
        method: 'post',
        url: `/api/conversations/${conversationId}/${isMuted ? 'unmute' : 'mute'}`,
        successToast: isMuted ? 'Conversation unmuted' : 'Conversation muted',
        errorToast: 'Could not update mute state',
        mode: 'after-mutate',
        silent: !!opts.silent,
      }),
    [runAction, conversationId, isMuted],
  );

  const accept = useCallback(
    (opts = {}) =>
      runAction({
        action: 'accept',
        method: 'patch',
        url: `/api/conversations/${conversationId}`,
        body: { action: 'accept' },
        successToast: 'Request accepted',
        errorToast: 'Could not accept',
        mode: 'after-mutate',
        silent: !!opts.silent,
      }),
    [runAction, conversationId],
  );

  const decline = useCallback(
    (opts = {}) =>
      runAction({
        action: 'decline',
        method: 'patch',
        url: `/api/conversations/${conversationId}`,
        body: { action: 'decline' },
        successToast: 'Request declined',
        errorToast: 'Could not decline',
        mode: 'conversation-gone',
        silent: !!opts.silent,
      }),
    [runAction, conversationId],
  );

  const leaveGroup = useCallback((opts = {}) => {
    if (!currentUserId) {
      toast.error('Could not leave group');
      return Promise.resolve(false);
    }

    return runAction({
      action: 'leave-group',
      method: 'delete',
      url: `/api/conversations/${conversationId}/participants/${currentUserId}`,
      successToast: 'You left the group',
      errorToast: 'Could not leave group',
      mode: 'conversation-gone',
      silent: !!opts.silent,
    });
  }, [runAction, conversationId, currentUserId]);

  // ── Available-actions matrix ──────────────────────────────────────────
  // Lets consumers render menus declaratively instead of repeating the
  // same boolean logic in every file.
  const availableActions = useMemo(() => {
    const convo = conversation;
    const status = convo?.status || 'active';
    const isGroup = !!convo?.is_group;
    const muted = !!convo?.muted;
    const isPending = status === 'pending';
    const isBlocked = status === 'blocked';
    const isArchived = !!convo?.archived;
    const isInitiator = !!convo && convo.initiated_by === currentUserId;

    return {
      // Engagement
      canNudge: !!convo && !isPending && !isBlocked,
      // Mute toggle is always allowed when conversation exists.
      canMute: !!convo && !muted,
      canUnmute: !!convo && muted,
      // Inbox state
      canArchive: !!convo && !isArchived,
      canUnarchive: !!convo && isArchived,
      canDeleteForMe: !!convo,
      canRestoreForMe: !!convo,
      // Safety
      canBlock: !!convo && !isGroup && !isBlocked,
      // Requests (only the recipient sees accept/decline; the sender sees a pending hint)
      canAcceptRequest: !!convo && isPending && !isInitiator,
      canDeclineRequest: !!convo && isPending && !isInitiator,
      // Group membership
      canLeaveGroup: !!convo && isGroup,
    };
  }, [conversation, currentUserId]);

  return {
    busy,
    availableActions,
    nudge,
    archive,
    unarchive,
    deleteForMe,
    restoreForMe,
    block,
    unblock,
    toggleMute,
    accept,
    decline,
    leaveGroup,
  };
}

/**
 * useBulkConversationActions — companion to `useConversationActions`
 * for multi-conversation operations. Calls the existing backend
 * `bulk-*` endpoints directly (no per-conversation loops, no new
 * routes) so we keep the same response shape and any backend-side
 * partial-success accounting.
 *
 * Each action returns `Promise<boolean>` — true on 2xx, false on
 * error. The hook surfaces toast feedback by default; pass
 * `{ silent: true }` if the consumer wants to render its own toast
 * (e.g. the MessagesPage bulk bar surfaces a custom Sonner toast
 * with an Undo action button).
 *
 * Endpoints reused:
 *   POST /api/conversations/bulk-archive          { conversation_ids }
 *   POST /api/conversations/bulk-delete-for-me    { conversation_ids }
 *   POST /api/conversations/bulk-restore-for-me   { conversation_ids }
 */
export function useBulkConversationActions() {
  const [busy, setBusy] = useState(false);

  const runBulk = useCallback(async ({
    ids,
    url,
    successToast,
    errorToast,
    silent = false,
  }) => {
    const list = Array.from(ids || []).filter(Boolean);
    if (list.length === 0) return false;

    setBusy(true);
    try {
      await axios.post(`${API_URL}${url}`, { conversation_ids: list }, AX);
      if (successToast && !silent) toast.success(successToast);
      return true;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || errorToast || 'Bulk action failed');
      return false;
    } finally {
      setBusy(false);
    }
  }, []);

  const bulkArchive = useCallback(
    (ids, opts = {}) => runBulk({
      ids,
      url: '/api/conversations/bulk-archive',
      successToast: `Archived ${(ids?.length || 0)} conversation${(ids?.length === 1) ? '' : 's'}`,
      errorToast: 'Could not archive',
      silent: !!opts.silent,
    }),
    [runBulk],
  );

  const bulkDeleteForMe = useCallback(
    (ids, opts = {}) => runBulk({
      ids,
      url: '/api/conversations/bulk-delete-for-me',
      successToast: `Removed ${(ids?.length || 0)} from your inbox`,
      errorToast: 'Could not remove',
      silent: !!opts.silent,
    }),
    [runBulk],
  );

  const bulkRestoreForMe = useCallback(
    (ids, opts = {}) => runBulk({
      ids,
      url: '/api/conversations/bulk-restore-for-me',
      successToast: `Restored ${(ids?.length || 0)} conversation${(ids?.length === 1) ? '' : 's'}`,
      errorToast: 'Could not restore',
      silent: !!opts.silent,
    }),
    [runBulk],
  );

  return {
    busy,
    bulkArchive,
    bulkDeleteForMe,
    bulkRestoreForMe,
  };
}
